import React, { useState } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { Expander } from '@amzn/stencil-react-components/expander';
import { useAppSelector } from "src/state/hooks";
import { Input } from "@amzn/stencil-react-components/form";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import jsonLogic from 'json-logic-js';
import { Regex } from "src/constant/constants";
import "src/components/commonStyles.scss";

const STRINGS_DISPLAYED = {
  test_event_rule_expander_title: "Test your event rule",
  rule_output_lable: "Rule output",
  execute_rule_button_text: "Execute rule",
}

/**
 * Renders an expander to let users test the json logic based event rules that they have defined.
 */
export const TestEventRuleExpander = () => {
  const eventModelState = useAppSelector(({eventModel}) => eventModel);

  const [variableNames, setVariableNames] = useState<string[]>([]);
  const [variableMap, setVariableMap] = useState<Record<string, string>>({});
  const [ruleOutput, setRuleOutput] = useState<string>("");

  // render variable keys based on the event rule entered
  const onExpanderToggle = (isExpanded: boolean) => {
    if (isExpanded) {
      const minifiedEventRule = JSON.stringify(JSON.parse(eventModelState.eventRule));

      const distinctVariables = new Set<string>();
      minifiedEventRule // {"and":[{"==":[{"var":"country"},"USA"]}, {"==":[{"var":"isManager"},true]}]}
        .match(Regex.JSONLogicVariablePattern) // ['"var":"country"', '"var":"isManager"']
        .map(variable => variable.split(":")[1]?.replace(/"/g, '')) // ['country', 'isManager']
        .forEach(variable => distinctVariables.add(variable));

      setVariableNames(Array.from(distinctVariables));
    }
  }

  // evaluate json logic based event rule based on the variable values entered
  const evaluateJSONLogicRule = () => {
    /**
     * Parsing string values entered by users for each variable to handle non-string type variables.
     * Parsed values are required for the rule to be evaluated by `json-logic-js` library.
     * Example:
     * variablesMap = {
     *   "country": "USA",
     *   "isManager": "true",
     *   "popstarFeatureList": "['my_hr_my_support', 'myhr_ps_re']"
     * }
     * Then variablesToEvaluate = {
     *   "country": "USA",
     *   "isManager": true,
     *   "popstarFeatureList": ['my_hr_my_support', 'myhr_ps_re']
     * }
     */
    const variablesToEvaluate: Record<string, any> = {};
    Object.entries(variableMap).map((variableEntry) => {
      try {
        variablesToEvaluate[variableEntry[0]] = JSON.parse(variableEntry[1]);
      } catch(error) {
        // parsing error means the value is of string type
        // other types like boolean, arrays, objects are successfully parsed
        variablesToEvaluate[variableEntry[0]] = variableEntry[1];
      }
    });
    
    const ruleOutput: boolean = jsonLogic.apply(JSON.parse(eventModelState.eventRule), variablesToEvaluate)
    setRuleOutput(String(ruleOutput));
  }

  const renderTestEventRuleExpanderTitle = ({
      titleText = STRINGS_DISPLAYED.test_event_rule_expander_title,
      toggle }) => (
    <Text 
      onClick={toggle}
      fontSize={"20px"}
      color={"primary70"}
      className={"cursor_pointer"}
    >
      {titleText}
    </Text>
  );

  return <Expander
    titleText={STRINGS_DISPLAYED.test_event_rule_expander_title}
    renderTitle={renderTestEventRuleExpanderTitle}
    onToggle={onExpanderToggle}
  >
    <Col padding={{left: "12px"}} gridGap={"16px"}>
      {variableNames && variableNames.map((variableName) => {
        return <Row
          gridGap={"16px"} 
          alignItems={"center"} 
          key={`event-rule-variable-${variableName}`}
          justifyContent={"space-between"}
        >
          <Label fontSize={"14px"} htmlFor={`${variableName}-input`}>
            {variableName}
          </Label>
          <Input
            id={`${variableName}-input`}
            onChange={(e) => {
              e?.persist();

              setVariableMap((prevState) => {
                let newState = {
                  ...prevState,
                }

                newState[`${variableName}`] = e?.target?.value;
                return newState;
              });
            }}
            value={variableMap[`${variableName}`]}
            width={"300px"}
          />
        </Row>
      })}

      <Row
        gridGap={"16px"} 
        alignItems={"center"} 
        justifyContent={"start"}
      >
        <Label fontSize={"14px"} fontWeight={"bold"} htmlFor={"rule-output"}>
          {STRINGS_DISPLAYED.rule_output_lable}
        </Label>
        <Text color={ruleOutput === "true" ? "green" : "red"} id={"rule-output"}>
          {ruleOutput}
        </Text>
      </Row>

      <Button
        variant={ButtonVariant.Primary}
        size={ButtonSize.Small}
        onClick={evaluateJSONLogicRule}
        className={"width_fit_content"}
      >
        {STRINGS_DISPLAYED.execute_rule_button_text}
      </Button>
    </Col>
  </Expander>
}
